import { useHttpClient } from '@/composables/useHttpClient'
import { useLogError } from '@/composables/useLogError'
import { useAccountStore } from '@/stores/useAccountStore'
import { useAppStore } from '@/stores/useAppStore'
import { useCubeStore } from '@/stores/useCubeStore'
import { useCurrentReadsStore } from '@/stores/useCurrentReadsStore'
import { useFormsStore } from '@/stores/useFormsStore'
import { useMapStore } from '@/stores/useMapStore'
import { useSettingsStore } from '@/stores/useSettingsStore'
import strings from '@/util/Strings'
import Axios from 'axios'
import Cookie from 'js-cookie'
import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'

export const useAuthStore = defineStore('auth', () => {
  const { logError } = useLogError()
  const { reemApi } = useHttpClient()

  // --- Constants ---
  const COOKIE_NAME = 'efToken'
  const COOKIE_LIFETIME = 365 * 10

  // --- State ---
  const userName: Ref<string | null> = ref(null)
  const backendAccessToken: Ref<string | null> = ref(null)
  const userIsAuthenticated: Ref<boolean> = ref(false)
  const errorMessage: Ref<string | null> = ref(null)
  const strapiToken = import.meta.env.VITE_STRAPI_TOKEN

  // Static config (not reactive — set once from env)
  const crm = {
    clientId: import.meta.env.VITE_CLIENT_ID,
  }

  const urls = {
    authentication: import.meta.env.VITE_AUTH_URL_BASE,
    reem: import.meta.env.VITE_REEM_URL,
    lukas: import.meta.env.VITE_LUKAS_URL,
    backend: import.meta.env.VITE_BACKEND_URL,
    deviceManager: import.meta.env.VITE_DEVICE_MANAGER_URL,
    strapiBaseUrl: import.meta.env.VITE_STRAPI_URL,
  }

  // --- Computed ---
  const authCookie = computed(() => Cookie.get(COOKIE_NAME))

  // --- Internal helpers ---

  function setToken(token) {
    backendAccessToken.value = token
    Cookie.set(COOKIE_NAME, token, { expires: COOKIE_LIFETIME, secure: true })
  }

  function clearAuth() {
    Cookie.remove(COOKIE_NAME)
    userIsAuthenticated.value = false
    userName.value = null
    errorMessage.value = null
    backendAccessToken.value = null
    // Reset all Pinia stores on logout.
    useAppStore().reset()
    useCubeStore().reset()
    useAccountStore().reset()
    useCurrentReadsStore().reset()
    useSettingsStore().reset()
    useMapStore().reset()
  }

  // --- Actions ---

  async function login({ user, pwd, router }) {
    const forms = useFormsStore()
    try {
      forms.setFormState('login', 'submitting')

      const formData = new FormData()
      formData.append('user', user)
      formData.append('pwd', pwd)
      formData.append('authorized_yes', '1')

      const appUrl = import.meta.env.VITE_APP_URL || window.location.origin
      const authUrl = new URL(urls.authentication)
      authUrl.searchParams.append('scope', '1')
      authUrl.searchParams.append('response_type', 'token')
      authUrl.searchParams.append('redirect_uri', appUrl)
      authUrl.searchParams.append('client_id', crm.clientId)
      authUrl.searchParams.append('getaccesstokenjson', '1')

      const result = await Axios.post(authUrl.href, formData)

      if (result?.data?.error_description) {
        errorMessage.value = result.data.error_description
        forms.setFormState('login', false)
      } else if (result?.data?.access_token) {
        errorMessage.value = ''
        setToken(result.data.access_token)
        userIsAuthenticated.value = true
        useAppStore().init({ router })
      } else {
        forms.setFormState('login', false)
      }
    } catch (err: any) {
      if (err.response?.status === 401 && err.response?.data?.error_description) {
        errorMessage.value = err.response.data.error_description
      } else {
        logError('The authenticateUser request failed: ' + err)
      }
      forms.setFormState('login', false)
    }
  }

  async function logout() {
    const forms = useFormsStore()
    try {
      forms.setFormState('logout', 'submitting')
      await reemApi.post('/users/logout')
    } catch (err) {
      logError('The logout request failed: ' + err)
    }
    clearAuth()
    forms.setFormState('logout', false)
  }

  function doAutologin(accessToken) {
    if (!accessToken) { return }
    setToken(accessToken)
    userIsAuthenticated.value = true
  }

  function loadPersistedToken() {
    // CRM autologin: access_token in URL fragment.
    const match = location.hash.match(/access_token=([^&]*)/)
    if (match?.[1]) {
      doAutologin(match[1])
      return
    }

    // Regular session: read token from cookie.
    if (authCookie.value) {
      backendAccessToken.value = authCookie.value
      userIsAuthenticated.value = true
    }
  }

  async function startPasswordReset(email) {
    const forms = useFormsStore()
    try {
      forms.setFormState('passwordReset', 'submitting')

      const data = new FormData()
      data.append('user', email)
      await Axios.post(
        `${urls.authentication}/v001/forgotpasswordreset?client_id=${crm.clientId}`,
        data
      )

      const now = Math.floor(Date.now() / 1000)
      Cookie.set('lastPasswordReset', now, { expires: 1, secure: true })
      forms.setFormState('passwordReset', 'finished', strings.authentication.passwordResetMailSent)
    } catch (err) {
      logError(`Error starting pw reset: ${err}`)
      forms.setFormState('passwordReset', false)
    }
  }

  async function startChangePassword() {
    const forms = useFormsStore()
    try {
      forms.setFormState('changePassword', 'submitting')

      const email = useSettingsStore().contractData.email
      const data = new FormData()
      data.append('user', email)
      await Axios.post(
        `${urls.authentication}/v001/forgotpasswordreset?client_id=${crm.clientId}`,
        data
      )

      const now = Math.floor(Date.now() / 1000)
      Cookie.set('lastPasswordReset', now, { expires: 1, secure: true })
      forms.setFormState('changePassword', 'finished', strings.authentication.passwordResetMailSent)
    } catch (err) {
      logError(`Error starting change password: ${err}`)
      forms.setFormState('changePassword', false)
    }
  }

  async function changeUserPassword({ password, token }) {
    try {
      const formData = new FormData()
      formData.append('pwd', password)

      const appUrl = import.meta.env.VITE_APP_URL || window.location.origin
      const result = await Axios.post(
        `${urls.authentication}/v001/pwdreset`,
        formData,
        {
          params: {
            scope: 1,
            response_type: 'token',
            client_id: crm.clientId,
            redirect_uri: appUrl,
            getaccesstokenjson: 1,
            key: token,
          },
        }
      )

      Cookie.set(COOKIE_NAME, result.data.access_token, {
        expires: COOKIE_LIFETIME,
        secure: true,
      })
      window.location.href = '/'
    } catch (err) {
      logError(`Error resetting user password: ${err}`)
    }
  }

  return {
    // State
    userName,
    backendAccessToken,
    userIsAuthenticated,
    errorMessage,
    strapiToken,
    crm,
    urls,
    // Computed
    authCookie,
    // Constants
    COOKIE_NAME,
    // Actions
    login,
    logout,
    doAutologin,
    loadPersistedToken,
    startPasswordReset,
    startChangePassword,
    changeUserPassword,
    // Exposed for auth.js
    setToken,
    clearAuth,
  }
})
